// Iter 338 — Admin-UI für DSG/DSGVO-konformes Soft-Delete-Recovery.
// ────────────────────────────────────────────────────────────────────────
// Erlaubt einem Admin:
//   1. Alle Accounts zu sehen, die sich in den letzten 30 Tagen via
//      "Konto endgültig löschen" im Customer-Portal/Mobile-App selbst
//      gelöscht haben (Soft-Delete-Fenster).
//   2. Einen Account innerhalb der 30 Tage komplett wiederherzustellen
//      (Backend kopiert Daten aus `deleted_*` zurück in die Original-
//      Collections).
//   3. Einen Account auf expliziten Kunden-Wunsch sofort endgültig zu
//      löschen ("DSG-Forderung sofort löschen", Art. 17 DSGVO).
//
// Backend-Endpoints (siehe /app/backend/routes/admin_deleted_accounts_routes.py):
//   • GET  /api/admin/deleted-accounts
//   • POST /api/admin/deleted-accounts/{id}/restore
//   • POST /api/admin/deleted-accounts/{id}/purge
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Trash2,
  RotateCcw,
  AlertTriangle,
  Clock,
  Mail,
  User,
  ShieldOff,
  Search,
  RefreshCcw,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const DeletedAccountsPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/deleted-accounts`,
        { headers: authHeader() }
      );
      setAccounts(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          'Liste der gelöschten Accounts konnte nicht geladen werden'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRestore = async () => {
    if (!restoreTarget) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/deleted-accounts/${restoreTarget.id}/restore`,
        {},
        { headers: authHeader() }
      );
      toast.success(
        `Account ${restoreTarget.full_name || data.email || restoreTarget.email} wiederhergestellt — ${
          (data.restored_collections || []).length
        } Collections zurückgespielt`
      );
      setRestoreTarget(null);
      await load();
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          'Wiederherstellung fehlgeschlagen — bitte Logs prüfen'
      );
    } finally {
      setBusy(false);
    }
  };

  const handlePurge = async () => {
    if (!purgeTarget) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/deleted-accounts/${purgeTarget.id}/purge`,
        {},
        { headers: authHeader() }
      );
      toast.success(
        `Account ${purgeTarget.full_name || purgeTarget.email} endgültig gelöscht — DSG/DSGVO Art. 17 erfüllt`
      );
      setPurgeTarget(null);
      await load();
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          'Endgültige Löschung fehlgeschlagen'
      );
    } finally {
      setBusy(false);
    }
  };

  const filtered = accounts.filter((a) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (a.email || '').toLowerCase().includes(q) ||
      (a.full_name || '').toLowerCase().includes(q) ||
      (a.first_name || '').toLowerCase().includes(q) ||
      (a.last_name || '').toLowerCase().includes(q) ||
      (a.user_id || '').toLowerCase().includes(q) ||
      (a.customer_id || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="deleted-accounts-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <ShieldOff className="w-6 h-6 text-orange-500" />
            Gelöschte Konten
          </h1>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            DSG/DSGVO-Soft-Delete-Fenster: Kunden, die sich selbst gelöscht haben,
            können innerhalb von 30 Tagen vollständig wiederhergestellt werden.
            Danach werden alle Daten endgültig entfernt.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={load}
          disabled={loading}
          data-testid="refresh-deleted-accounts-btn"
        >
          <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Aktualisieren
        </Button>
      </div>

      <div className="mb-4 relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input
          placeholder="Suche Name / E-Mail / User-ID / Customer-ID …"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid="deleted-accounts-search"
        />
      </div>

      {loading && (
        <div className="text-center text-gray-500 py-12" data-testid="loading-state">
          Lade gelöschte Konten …
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-gray-500" data-testid="empty-state">
            <ShieldOff className="w-10 h-10 mx-auto mb-3 text-gray-400" />
            {search
              ? 'Keine Einträge passen zu deiner Suche.'
              : 'Keine weichgelöschten Konten — alles sauber.'}
          </CardContent>
        </Card>
      )}

      {!loading && filtered.length > 0 && (
        <div className="space-y-3">
          {filtered.map((a) => {
            const urgent =
              typeof a.days_until_purge === 'number' && a.days_until_purge <= 3;
            return (
              <Card
                key={a.id}
                className={urgent ? 'border-orange-400' : ''}
                data-testid={`deleted-account-${a.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      {/* Name + E-Mail prominent (Iter 338) */}
                      {(a.full_name || a.first_name || a.last_name) && (
                        <div className="text-lg font-semibold text-gray-900 truncate">
                          {a.full_name || `${a.first_name || ''} ${a.last_name || ''}`.trim()}
                        </div>
                      )}
                      <div className="flex items-center gap-2 flex-wrap mt-1">
                        <Mail className="w-4 h-4 text-gray-500 shrink-0" />
                        <span className="text-sm text-gray-700 truncate">
                          {a.email || (
                            <span className="italic text-gray-400">
                              (keine E-Mail gespeichert)
                            </span>
                          )}
                        </span>
                        {urgent && (
                          <Badge variant="destructive" className="text-xs">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Bald endgültig gelöscht
                          </Badge>
                        )}
                      </div>

                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-500">
                        <div className="truncate">
                          <User className="w-3 h-3 inline mr-1" />
                          User-ID:{' '}
                          <span className="font-mono">{a.user_id || '—'}</span>
                        </div>
                        <div className="truncate">
                          <User className="w-3 h-3 inline mr-1" />
                          Customer-ID:{' '}
                          <span className="font-mono">
                            {a.customer_id || '—'}
                          </span>
                        </div>
                        <div>
                          <Clock className="w-3 h-3 inline mr-1" />
                          Gelöscht am: {formatDate(a.deleted_at)}
                        </div>
                        <div>
                          <Clock className="w-3 h-3 inline mr-1" />
                          Endgültige Löschung: {formatDate(a.expire_at)}
                          {typeof a.days_until_purge === 'number' && (
                            <span
                              className={`ml-2 font-medium ${
                                urgent ? 'text-orange-600' : 'text-gray-700'
                              }`}
                            >
                              (in {a.days_until_purge} Tag
                              {a.days_until_purge === 1 ? '' : 'en'})
                            </span>
                          )}
                        </div>
                      </div>

                      {Array.isArray(a.collections_archived) &&
                        a.collections_archived.length > 0 && (
                          <details className="mt-2 text-xs">
                            <summary className="cursor-pointer text-gray-500 hover:text-gray-700">
                              {a.collections_archived.length} archivierte
                              Datenbereiche
                            </summary>
                            <ul className="mt-2 ml-4 list-disc text-gray-400 space-y-0.5">
                              {a.collections_archived.map((c, i) => (
                                <li key={i}>{c}</li>
                              ))}
                            </ul>
                          </details>
                        )}
                    </div>

                    <div className="flex items-center gap-2 shrink-0 flex-wrap">
                      <Button
                        size="sm"
                        onClick={() => setRestoreTarget(a)}
                        data-testid={`restore-btn-${a.id}`}
                      >
                        <RotateCcw className="w-4 h-4 mr-1.5" />
                        Wiederherstellen
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => setPurgeTarget(a)}
                        data-testid={`purge-btn-${a.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1.5" />
                        Sofort löschen
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* RESTORE-MODAL */}
      <Dialog
        open={!!restoreTarget}
        onOpenChange={(o) => !o && setRestoreTarget(null)}
      >
        <DialogContent data-testid="restore-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-green-600" />
              Konto wiederherstellen?
            </DialogTitle>
            <DialogDescription className="pt-2 space-y-2">
              <span className="block">
                Konto{' '}
                <strong>
                  {restoreTarget?.full_name || restoreTarget?.email}
                </strong>
                {restoreTarget?.full_name && restoreTarget?.email && (
                  <span className="text-gray-500"> ({restoreTarget.email})</span>
                )}{' '}
                wird komplett reaktiviert. Alle Termine, Mahlzeiten, Punkte und
                Rechnungen kommen zurück.
              </span>
              <span className="block text-xs text-gray-500">
                Der Kunde kann sich danach sofort wieder einloggen (per OTP oder
                Passwort, falls vorhanden).
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRestoreTarget(null)}
              disabled={busy}
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleRestore}
              disabled={busy}
              data-testid="confirm-restore-btn"
            >
              {busy ? 'Stelle wieder her …' : 'Ja, wiederherstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PURGE-MODAL (gefährlich, rot) */}
      <Dialog
        open={!!purgeTarget}
        onOpenChange={(o) => !o && setPurgeTarget(null)}
      >
        <DialogContent data-testid="purge-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Endgültig löschen?
            </DialogTitle>
            <DialogDescription className="pt-2 space-y-2">
              <span className="block">
                Konto{' '}
                <strong>{purgeTarget?.full_name || purgeTarget?.email}</strong>
                {purgeTarget?.full_name && purgeTarget?.email && (
                  <span className="text-gray-500"> ({purgeTarget.email})</span>
                )}{' '}
                wird{' '}
                <strong className="text-red-600">JETZT unwiderruflich</strong>{' '}
                gelöscht. Diese Aktion ist nicht rückgängig zu machen.
              </span>
              <span className="block text-xs text-gray-500">
                Nutze diese Option nur bei einer expliziten Kundenanfrage
                &bdquo;sofort und vollständig löschen&ldquo; gemäss DSG/DSGVO Art. 17.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPurgeTarget(null)}
              disabled={busy}
            >
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              onClick={handlePurge}
              disabled={busy}
              data-testid="confirm-purge-btn"
            >
              {busy ? 'Lösche endgültig …' : 'Ja, endgültig löschen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeletedAccountsPage;
